/**
 * PaymentView
 *
 * Stripe-inspired payment page with split layout:
 * - Left panel: Payment method selector + Moyasar form
 * - Right panel: Order summary with amount breakdown
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useBreakPoinst from '../../../../hooks/useBreakPoinst';
import { useSubscriptionPayment } from '../../hooks';
import { MoyasarPaymentForm, TransactionStatusIndicator } from '../../components';
import { T_KEYS } from '../../constants';
import {
  PaymentPageContainer,
  PaymentPageContent,
  PaymentFormPanel,
  OrderSummaryPanel,
  OrderSummaryTitle,
  OrderSummaryAmount,
  OrderSummaryCurrency,
  OrderSummaryRow,
  OrderSummaryLabel,
  OrderSummaryValue,
  OrderSummaryTotal,
  OrderSummaryTotalLabel,
  OrderSummaryTotalValue,
  PaymentSectionTitle,
  PaymentSectionSubtitle,
  PaymentFormWrapper,
  BackButtonRow,
  BackButtonText,
  SecureBadge,
} from '../../styles';

const base = window.location.origin;

const PaymentView = () => {
  const { id: planId, period } = useParams<{ id: string; period: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isPhone } = useBreakPoinst();
  const callbackUrl = `${base}/subscription-payment-callback`;

  const {
    paymentConfig,
    isInitiating,
    isProcessing,
    initError,
    initiatePayment,
    handlePaymentCompleted,
  } = useSubscriptionPayment({
    planId: Number(planId),
  });

  // Initiate payment on mount
  useEffect(() => {
    if (planId) {
      initiatePayment();
    }
  }, [planId]);

  // Loading state
  if (isInitiating) {
    return (
      <PaymentPageContainer>
        <TransactionStatusIndicator status="loading" />
      </PaymentPageContainer>
    );
  }

  // Error state
  if (initError) {
    return (
      <PaymentPageContainer>
        <TransactionStatusIndicator
          status="failed"
          onRetry={initiatePayment}
          onGoHome={() => navigate('/')}
        />
      </PaymentPageContainer>
    );
  }

  // Processing (after on_completed, before redirect)
  if (isProcessing) {
    return (
      <PaymentPageContainer>
        <TransactionStatusIndicator status="processing" />
      </PaymentPageContainer>
    );
  }

  // Payment form
  if (!paymentConfig) return null;

  const moyasarMethods: ('creditcard' | 'applepay')[] = ['applepay', 'creditcard'];

  const amount = paymentConfig.amount;
  const periodLabel = period === 'Yearly' ? t(T_KEYS.YEARLY) : t(T_KEYS.MONTHLY);

  return (
    <PaymentPageContainer>
      <PaymentPageContent $isPhone={isPhone}>
        {/* Left Panel — Payment Form */}
        <PaymentFormPanel $isPhone={isPhone}>
          <BackButtonRow onClick={() => navigate(-1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="#6b7c93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <BackButtonText>{t(T_KEYS.BACK)}</BackButtonText>
          </BackButtonRow>

          <PaymentSectionTitle>{t(T_KEYS.PAYMENT_DETAILS)}</PaymentSectionTitle>
          <PaymentSectionSubtitle>{t(T_KEYS.PAYMENT_SUBTITLE)}</PaymentSectionSubtitle>

          <PaymentFormWrapper>
            <MoyasarPaymentForm
              key={i18n.language}
              paymentConfig={paymentConfig}
              callbackUrl={`${callbackUrl}?transactionGuid=${paymentConfig.transactionGuid}`}
              methods={moyasarMethods}
              language={i18n.language === 'ar' ? 'ar' : 'en'}
              onCompleted={handlePaymentCompleted}
            />
          </PaymentFormWrapper>

          <SecureBadge>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {t(T_KEYS.SECURE_PAYMENT)}
          </SecureBadge>
        </PaymentFormPanel>

        {/* Right Panel — Order Summary */}
        <OrderSummaryPanel $isPhone={isPhone}>
          <OrderSummaryTitle>{t(T_KEYS.ORDER_SUMMARY)}</OrderSummaryTitle>

          <OrderSummaryAmount>
            {amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            <OrderSummaryCurrency>{t('COMMON.RS')}</OrderSummaryCurrency>
          </OrderSummaryAmount>

          <div style={{ marginTop: 24 }}>
            <OrderSummaryRow>
              <OrderSummaryLabel>{t(T_KEYS.PLAN)}</OrderSummaryLabel>
              <OrderSummaryValue>{t(T_KEYS.SUBSCRIPTION_PLAN)}</OrderSummaryValue>
            </OrderSummaryRow>

            <OrderSummaryRow>
              <OrderSummaryLabel>{t(T_KEYS.BILLING_PERIOD)}</OrderSummaryLabel>
              <OrderSummaryValue>{periodLabel}</OrderSummaryValue>
            </OrderSummaryRow>

            <OrderSummaryRow>
              <OrderSummaryLabel>{t(T_KEYS.CURRENCY)}</OrderSummaryLabel>
              <OrderSummaryValue>{paymentConfig.currency}</OrderSummaryValue>
            </OrderSummaryRow>

            <OrderSummaryTotal>
              <OrderSummaryTotalLabel>{t(T_KEYS.TOTAL)}</OrderSummaryTotalLabel>
              <OrderSummaryTotalValue>
                {amount.toLocaleString('en-US', { minimumFractionDigits: 2 })} {t('COMMON.RS')}
              </OrderSummaryTotalValue>
            </OrderSummaryTotal>
          </div>
        </OrderSummaryPanel>
      </PaymentPageContent>
    </PaymentPageContainer>
  );
};

export default PaymentView;
